import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import { useSearchParams } from 'react-router'

import { httpOk, GET, PUT } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import { businessNow } from 'helpers/businessDay'
import { hasPermission } from 'helpers/permissions'
import { useUnsavedChangesGuard } from 'pages/parts/useUnsavedChangesGuard'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsFilters from 'pages/main/analytics/parts/useAnalyticsFilters'
import { buildAnalyticsUrl, parseLocalDate } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { FilterField, useChainLoad } from '../parts/ChainFilterBar'
import { downloadExcel } from '../parts/chainExcel'
import { money, chainColor, TableSearch, ChainEmpty } from '../parts/chainUi'

// Экран «Планы»: план продаж (с разрезами) и расходов на месяц для всех филиалов сразу.
// Бэк — ChainPlansController (GET/PUT /chain/plans), хранение — sales_plans и payment_purpose_plan.

const DIMENSIONS = ['total', 'order_type', 'channel', 'source', 'category']
const TOTAL_METRICS = ['revenue', 'cheques', 'avg_cheque', 'guests', 'items', 'gross_profit', 'margin', 'food_cost', 'net_profit']
const RATIO_METRICS = ['avg_cheque', 'margin', 'food_cost']
const PERCENT_METRICS = ['margin', 'food_cost']

function currentMonth() {
	const now = businessNow()
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function monthOf(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function shiftMonth(month, delta) {
	const [year, value] = month.split('-').map(Number)
	const date = new Date(year, value - 1 + delta, 1)
	return monthOf(date)
}

function salesKey(posId, dimension, dimensionId, metric) {
	return `s|${posId}|${dimension}|${dimensionId}|${metric}`
}

function expenseKey(posId, purposeId) {
	return `e|${posId}|${purposeId}`
}

// Значение поля → число; пусто — null. Пробелы-разделители и запятая как точка
function parseValue(value) {
	if (value === null || value === undefined) return null
	const text = String(value).replace(/\s/g, '').replace(',', '.')
	if (text === '') return null
	const number = Number(text)
	return isNaN(number) ? null : number
}

function sameValue(a, b) {
	const left = parseValue(a)
	const right = parseValue(b)
	if (!left && !right) return true
	return left === right
}

function formatInput(value, isPercent) {
	const number = parseValue(value)
	if (number === null) return ''
	return isPercent ? String(number).replace('.', ',') : formatMoney(Math.round(number))
}

function Plans() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [base, setBase] = useState({})
	const [draft, setDraft] = useState({})
	// ?tab=expenses — сразу на расходы (старый адрес «План расходов» ведёт сюда)
	const [searchParams] = useSearchParams()
	// Без права «Планы» (chain_plans) — только план расходов: экран открыт и по праву старого
	// «План расходов» (expense_plans), а планы продаж такому сотруднику сервер сохранить не даст
	const canSales = hasPermission('chain_plans')
	const [tab, setTab] = useState(!canSales || searchParams.get('tab') === 'expenses' ? 'expenses' : 'sales')
	const [dimension, setDimension] = useState('total')
	const [metric, setMetric] = useState('revenue')
	const [reference, setReference] = useState('prev')
	const [growth, setGrowth] = useState('10')
	const [onlyEmpty, setOnlyEmpty] = useState(true)
	const [showAll, setShowAll] = useState(false)
	const [search, setSearch] = useState('')

	const [runLoad, loading] = useChainLoad(searchWithFilter)
	// ?month=2026-10 — открыть сразу нужный месяц (ссылка из «Плана и факта»)
	const monthParam = searchParams.get('month')
	const rawFilters = useAnalyticsFilters({ pos_ids: [], month: /^\d{4}-(0[1-9]|1[0-2])$/.test(monthParam || '') ? monthParam : currentMonth() }, runLoad)

	const changes = useMemo(() => {
		const keys = new Set([...Object.keys(base), ...Object.keys(draft)])
		return [...keys].filter(key => !sameValue(base[key], draft[key]))
	}, [base, draft])
	const dirty = changes.length > 0

	useUnsavedChangesGuard(dirty, { message: t('unsaved_changes_confirm') })

	// Смена месяца или филиалов перезагружает экран — несохранённое спрашиваем
	const filters = {
		...rawFilters,
		loading,
		apply: (next) => {
			if (dirty && !window.confirm(t('unsaved_changes_confirm'))) return
			return rawFilters.apply(next)
		},
		reset: () => {
			if (dirty && !window.confirm(t('unsaved_changes_confirm'))) return
			return rawFilters.reset()
		},
	}
	const { draft: filterDraft, patch, applied } = rawFilters

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/plans', filter), {}, { loader: false })
		if (!httpOk(response)) return
		const next = {}
		;(response.data.plans || []).forEach(row => {
			next[salesKey(row.pos_id, row.dimension, row.dimension_id, row.metric)] = row.value
		})
		;(response.data.expense_plans || []).forEach(row => {
			next[expenseKey(row.pos_id, row.purpose_id)] = row.value
		})
		setData(response.data)
		setBase(next)
		setDraft(next)
	}

	async function save() {
		const sales = []
		const expenses = []
		changes.forEach(key => {
			const parts = key.split('|')
			const value = parseValue(draft[key])
			if (parts[0] === 's') {
				sales.push({ pos_id: Number(parts[1]), dimension: parts[2], dimension_id: Number(parts[3]), metric: parts[4], value: value ?? '' })
			} else {
				expenses.push({ pos_id: Number(parts[1]), purpose_id: Number(parts[2]), value: value ?? '' })
			}
		})
		const response = await PUT('/chain/plans', { month: data.month, sales, expenses }, { loader: true })
		if (httpOk(response)) {
			// Сохранённое становится базой — guard отпускает, перечитываем с сервера
			setBase(draft)
			searchWithFilter(applied)
		}
	}

	function setValue(key, value) {
		setDraft(prev => ({ ...prev, [key]: value }))
	}

	const poses = useMemo(() => data?.poses || [], [data])
	const dimensions = useMemo(() => data?.dimensions || {}, [data])
	const metricsOf = useMemo(() => data?.metrics || {}, [data])

	// Факт-подсказка: прошлый месяц или тот же месяц год назад
	const refData = data?.reference?.[reference]
	const refMap = useMemo(() => {
		const map = {}
		;(refData?.sales || []).forEach(row => {
			map[salesKey(row.pos_id, row.dimension, row.dimension_id, row.metric)] = row.value
		})
		;(refData?.expenses || []).forEach(row => {
			map[expenseKey(row.pos_id, row.purpose_id)] = row.value
		})
		return map
	}, [refData])

	const metricLabel = (id) => t(`chain_plan_m_${id}`)
	const dimensionLabel = (id) => t(`chain_plan_d_${id}`)

	// Строки текущего вида: [{ id, name, depth }] значений разреза (у «Филиала целиком» — филиалы)
	const dimensionRows = useMemo(() => {
		if (dimension === 'total') return []
		const list = dimensions[dimension] || []
		const query = search.trim().toLowerCase()
		return list.filter(item => {
			if (query && !String(item.name).toLowerCase().includes(query)) return false
			if (showAll || query) return true
			// По умолчанию — значения, где есть план или были продажи: пустые каналы не мешают
			return poses.some(pos => parseValue(draft[salesKey(pos.id, dimension, item.id, metric)]) || refMap[salesKey(pos.id, dimension, item.id, 'revenue')])
		})
	}, [dimension, dimensions, search, showAll, poses, draft, metric, refMap])

	const purposeRows = useMemo(() => {
		const list = data?.expense_purposes || []
		const query = search.trim().toLowerCase()
		return list.filter(item => {
			if (query && !String(item.name).toLowerCase().includes(query)) return false
			if (showAll || query) return true
			return poses.some(pos => parseValue(draft[expenseKey(pos.id, item.id)]) || refMap[expenseKey(pos.id, item.id)])
		})
	}, [data, search, showAll, poses, draft, refMap])

	// Ячейки текущего вида — для «заполнить по факту», «скопировать», «очистить»
	function viewKeys() {
		if (tab === 'expenses') {
			return purposeRows.flatMap(item => poses.map(pos => expenseKey(pos.id, item.id)))
		}
		if (dimension === 'total') {
			return poses.flatMap(pos => TOTAL_METRICS.map(id => salesKey(pos.id, 'total', 0, id)))
		}
		return dimensionRows.flatMap(item => poses.map(pos => salesKey(pos.id, dimension, item.id, metric)))
	}

	function fillByFact() {
		const percent = Number(String(growth).replace(',', '.')) || 0
		const next = { ...draft }
		viewKeys().forEach(key => {
			if (onlyEmpty && parseValue(next[key])) return
			const fact = refMap[key]
			if (fact === undefined || fact === null || Number(fact) <= 0) return
			const metricId = key.split('|')[4]
			// Проценты (рентабельность, фудкост) переносим как есть, суммы — с ростом
			next[key] = PERCENT_METRICS.includes(metricId)
				? Math.round(Number(fact) * 10) / 10
				: Math.round(Number(fact) * (1 + percent / 100))
		})
		setDraft(next)
	}

	async function copyPreviousMonth() {
		if (!window.confirm(t('chain_plans_copy_confirm'))) return
		const response = await GET(buildAnalyticsUrl('/chain/plans', { ...applied, month: shiftMonth(applied.month, -1) }), {}, { loader: true })
		if (!httpOk(response)) return
		const source = {}
		;(response.data.plans || []).forEach(row => {
			source[salesKey(row.pos_id, row.dimension, row.dimension_id, row.metric)] = row.value
		})
		;(response.data.expense_plans || []).forEach(row => {
			source[expenseKey(row.pos_id, row.purpose_id)] = row.value
		})
		const prefix = tab === 'expenses' ? 'e|' : 's|'
		const next = { ...draft }
		Object.keys(next).forEach(key => {
			if (key.startsWith(prefix)) next[key] = ''
		})
		Object.entries(source).forEach(([key, value]) => {
			if (key.startsWith(prefix)) next[key] = value
		})
		setDraft(next)
	}

	function clearView() {
		if (!window.confirm(t('chain_plans_clear_confirm'))) return
		const next = { ...draft }
		viewKeys().forEach(key => { next[key] = '' })
		setDraft(next)
	}

	function exportExcel() {
		if (!data) return
		const value = (key) => parseValue(draft[key])
		const sheets = [{
			name: dimensionLabel('total'),
			title: `${t('chain_plans_title')} — ${data.month.split('-').reverse().join('.')}`,
			columns: [{ label: t('chain_branch'), type: 'text', width: 30 }, ...TOTAL_METRICS.map(id => ({
				label: metricLabel(id), type: PERCENT_METRICS.includes(id) ? 'percent' : 'money',
			}))],
			rows: poses.map(pos => [pos.name, ...TOTAL_METRICS.map(id => value(salesKey(pos.id, 'total', 0, id)))]),
		}]
		DIMENSIONS.filter(id => id !== 'total').forEach(dim => {
			const items = (dimensions[dim] || []).filter(item => poses.some(pos => (metricsOf[dim] || []).some(m => value(salesKey(pos.id, dim, item.id, m)))))
			if (items.length === 0) return
			const metricIds = metricsOf[dim] || []
			sheets.push({
				name: dimensionLabel(dim),
				title: `${t('chain_plans_title')} — ${dimensionLabel(dim)}`,
				columns: [
					{ label: dimensionLabel(dim), type: 'text', width: 28 },
					{ label: t('chain_branch'), type: 'text', width: 26 },
					...metricIds.map(id => ({ label: metricLabel(id), type: 'money' })),
				],
				rows: items.flatMap(item => poses.map(pos => [item.name, pos.name, ...metricIds.map(id => value(salesKey(pos.id, dim, item.id, id)))])),
			})
		})
		const purposes = (data.expense_purposes || []).filter(item => poses.some(pos => value(expenseKey(pos.id, item.id))))
		if (purposes.length > 0) {
			sheets.push({
				name: t('expenses'),
				title: `${t('chain_plans_title')} — ${t('expenses')}`,
				columns: [{ label: t('chain_purpose'), type: 'text', width: 32 }, ...poses.map(pos => ({ label: pos.name, type: 'money' }))],
				rows: purposes.map(item => [item.name, ...poses.map(pos => value(expenseKey(pos.id, item.id)))]),
			})
		}
		downloadExcel(`${t('chain_plans_title')} ${data.month}`, sheets)
	}

	const availableMetrics = dimension === 'total' ? TOTAL_METRICS : (metricsOf[dimension] || ['revenue'])
	const activeMetric = availableMetrics.includes(metric) ? metric : availableMetrics[0]

	return (
		<>
			<ChainFilterBar
				title={t('chain_plans_title')}
				icon="uil-calendar-alt"
				subtitle={data ? data.month.split('-').reverse().join('.') : null}
				filters={filters}
				presets={false}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
				actions={
					<button type="button" className="btn btn-sm btn-primary" style={{ borderRadius: '10px', fontWeight: 600 }}
						disabled={!dirty} onClick={save}>
						<i className="uil uil-save me-1"></i>
						{dirty ? `${t('save')} (${changes.length})` : t('chain_plans_saved')}
					</button>
				}
				tabs={[
					...(canSales ? [{ id: 'sales', label: t('chain_section_sales'), icon: 'uil-chart-growth' }] : []),
					{ id: 'expenses', label: t('expenses'), icon: 'uil-money-withdraw' },
				]}
				activeTab={tab}
				onTabChange={(id) => { setTab(id); setSearch('') }}
			>
				<FilterField label={t('month')}>
					<div className="calendar-input">
						<DatePicker
							showMonthYearPicker
							className="form-control"
							dateFormat="MM.yyyy"
							portalId="chain-month-picker"
							selected={parseLocalDate(`${filterDraft.month}-01`)}
							onChange={(date) => date && patch({ month: monthOf(date) })}
						/>
						<i className="uil uil-calendar-alt"></i>
					</div>
				</FilterField>
			</ChainFilterBar>

			{data && (
				<div className="card chain-card mb-3">
					<div className="card-body">
						{tab === 'sales' && (
							<div className="chain-toolbar">
								<div className="chain-toolbar-group">
									<FilterSegment
										options={DIMENSIONS.map(id => ({ id, label: dimensionLabel(id) }))}
										value={dimension}
										onChange={(id) => { setDimension(id); setSearch('') }}
									/>
								</div>
								{dimension !== 'total' && (
									<div className="chain-toolbar-group">
										<span className="chain-muted">{t('chain_plans_metric')}</span>
										<FilterSegment
											options={availableMetrics.map(id => ({ id, label: metricLabel(id) }))}
											value={activeMetric}
											onChange={setMetric}
										/>
									</div>
								)}
							</div>
						)}

						<div className="chain-plans-tools">
							<div className="chain-toolbar-group">
								<span className="chain-muted">{t('chain_plans_hint_source')}</span>
								<FilterSegment
									options={[
										{ id: 'prev', label: t('chain_plans_ref_prev') },
										{ id: 'year', label: t('chain_plans_ref_year') },
									]}
									value={reference}
									onChange={setReference}
								/>
								<div className="chain-plans-growth">
									<span>+</span>
									<input type="text" className="form-control" value={growth} onChange={(e) => setGrowth(e.target.value.replace(/[^\d,.-]/g, ''))} />
									<span>%</span>
								</div>
								<label className="chain-check">
									<input type="checkbox" className="form-check-input" checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} />
									{t('chain_plans_only_empty')}
								</label>
								<button type="button" className="btn btn-sm btn-outline-primary chain-plans-tool-btn" disabled={!refData} onClick={fillByFact}>
									<i className="uil uil-bolt-alt me-1"></i>
									{t('chain_plans_fill_by_fact')}
								</button>
							</div>
							<div className="chain-toolbar-group">
								<button type="button" className="btn btn-sm btn-outline-secondary chain-plans-tool-btn" onClick={copyPreviousMonth}>
									<i className="uil uil-copy me-1"></i>
									{t('chain_plans_copy_prev')}
								</button>
								<button type="button" className="btn btn-sm btn-outline-danger chain-plans-tool-btn" onClick={clearView}>
									<i className="uil uil-eraser me-1"></i>
									{t('chain_plans_clear')}
								</button>
							</div>
						</div>

						{(tab === 'expenses' || dimension !== 'total') && (
							<div className="chain-toolbar">
								<TableSearch value={search} onChange={setSearch} placeholder={tab === 'expenses' ? t('chain_search_purpose') : t('search')} />
								<label className="chain-check">
									<input type="checkbox" className="form-check-input" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
									{t('chain_plans_show_all')}
								</label>
							</div>
						)}

						{tab === 'sales' && dimension === 'total' && (
							<TotalTable poses={poses} draft={draft} base={base} refMap={refMap}
								metricLabel={metricLabel} onChange={setValue} />
						)}
						{tab === 'sales' && dimension !== 'total' && (
							<DimensionTable poses={poses} rows={dimensionRows} dimension={dimension} metric={activeMetric}
								draft={draft} base={base} refMap={refMap} onChange={setValue} />
						)}
						{tab === 'expenses' && (
							<ExpenseTable poses={poses} rows={purposeRows} draft={draft} base={base} refMap={refMap} onChange={setValue} />
						)}

						<div className="chain-muted mt-2">
							<i className="uil uil-info-circle me-1"></i>
							{t('chain_plans_footer_hint')}
						</div>
					</div>
				</div>
			)}
		</>
	)
}

// Поле плана: число с разделителями, под ним — факт для подсказки (клик подставляет)
function PlanCell({ cellKey, draft, base, refMap, onChange, isPercent = false, placeholder, row, col }) {
	const { t } = useTranslation()
	const [focused, setFocused] = useState(false)
	const value = draft[cellKey]
	const changed = !sameValue(base[cellKey], value)
	const fact = refMap[cellKey]

	function onKeyDown(e) {
		if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
		e.preventDefault()
		const next = document.querySelector(`[data-plan-row="${row + (e.key === 'ArrowUp' ? -1 : 1)}"][data-plan-col="${col}"]`)
		if (next) next.focus()
	}

	return (
		<td className={`chain-plan-cell ${changed ? 'changed' : ''}`}>
			<input
				type="text"
				inputMode="decimal"
				className="chain-plan-input"
				data-plan-row={row}
				data-plan-col={col}
				value={focused ? (value ?? '') : formatInput(value, isPercent)}
				placeholder={placeholder}
				onFocus={(e) => { setFocused(true); setTimeout(() => e.target.select(), 0) }}
				onBlur={() => setFocused(false)}
				onKeyDown={onKeyDown}
				onChange={(e) => onChange(cellKey, e.target.value.replace(/[^\d.,\s]/g, ''))}
			/>
			{fact !== undefined && fact !== null && (
				<button type="button" className="chain-plan-fact" title={t('chain_plans_use_fact')}
					onClick={() => onChange(cellKey, isPercent ? Math.round(Number(fact) * 10) / 10 : Math.round(Number(fact)))}>
					{isPercent ? `${String(Math.round(Number(fact) * 10) / 10).replace('.', ',')}%` : money(fact)}
				</button>
			)}
		</td>
	)
}

function TotalTable({ poses, draft, base, refMap, metricLabel, onChange }) {
	const { t } = useTranslation()
	const sum = (id) => poses.reduce((total, pos) => total + (parseValue(draft[salesKey(pos.id, 'total', 0, id)]) || 0), 0)
	const revenue = sum('revenue')
	const cheques = sum('cheques')

	return (
		<div className="chain-scroll">
			<table className="chain-table chain-plan-table">
				<thead>
					<tr>
						<th className="chain-name chain-sticky" style={{ left: 0 }}>{t('chain_branch')}</th>
						{TOTAL_METRICS.map(id => (
							<th key={id} className={`text-end ${RATIO_METRICS.includes(id) ? 'chain-plan-ratio-th' : ''}`}>
								{metricLabel(id)}{PERCENT_METRICS.includes(id) ? ', %' : ''}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{poses.length === 0 && <ChainEmpty colSpan={TOTAL_METRICS.length + 1} />}
					{poses.map((pos, rowIndex) => {
						const posRevenue = parseValue(draft[salesKey(pos.id, 'total', 0, 'revenue')])
						const posCheques = parseValue(draft[salesKey(pos.id, 'total', 0, 'cheques')])
						return (
							<tr key={pos.id}>
								<td className="chain-name chain-sticky" style={{ left: 0 }}>
									<span className="chain-dot" style={{ background: chainColor(rowIndex) }}></span>
									{pos.name}
								</td>
								{TOTAL_METRICS.map((id, colIndex) => (
									<PlanCell key={id} cellKey={salesKey(pos.id, 'total', 0, id)} draft={draft} base={base} refMap={refMap}
										onChange={onChange} isPercent={PERCENT_METRICS.includes(id)} row={rowIndex} col={colIndex}
										placeholder={id === 'avg_cheque' && posRevenue && posCheques ? formatMoney(Math.round(posRevenue / posCheques)) : ''} />
								))}
							</tr>
						)
					})}
				</tbody>
				{poses.length > 0 && (
					<tfoot>
						<tr>
							<td className="chain-sticky" style={{ left: 0 }}>{t('chain_network')}</td>
							{TOTAL_METRICS.map(id => (
								<td key={id} className="chain-num">
									{id === 'avg_cheque' ? (revenue && cheques ? money(revenue / cheques) : '—')
										: RATIO_METRICS.includes(id) ? '—' : money(sum(id))}
								</td>
							))}
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	)
}

function DimensionTable({ poses, rows, dimension, metric, draft, base, refMap, onChange }) {
	const { t } = useTranslation()
	const isRatio = RATIO_METRICS.includes(metric)
	const cell = (posId, id) => parseValue(draft[salesKey(posId, dimension, id, metric)]) || 0

	return (
		<div className="chain-scroll">
			<table className="chain-table chain-plan-table">
				<thead>
					<tr>
						<th className="chain-name chain-sticky" style={{ left: 0 }}>{t(`chain_plan_d_${dimension}`)}</th>
						{poses.map((pos, index) => (
							<th key={pos.id} className="text-end">
								<span className="chain-dot" style={{ background: chainColor(index) }}></span>
								{pos.name}
							</th>
						))}
						{!isRatio && <th className="text-end chain-total-col">{t('total')}</th>}
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 && <ChainEmpty colSpan={poses.length + 2} text={t('chain_plans_no_values')} />}
					{rows.map((item, rowIndex) => (
						<tr key={item.id}>
							<td className="chain-name chain-sticky" style={{ left: 0, paddingLeft: `${7 + (item.depth || 0) * 14}px` }}>
								{item.name}
							</td>
							{poses.map((pos, colIndex) => (
								<PlanCell key={pos.id} cellKey={salesKey(pos.id, dimension, item.id, metric)} draft={draft} base={base}
									refMap={refMap} onChange={onChange} row={rowIndex} col={colIndex} />
							))}
							{!isRatio && <td className="chain-num chain-total-col">{money(poses.reduce((total, pos) => total + cell(pos.id, item.id), 0))}</td>}
						</tr>
					))}
				</tbody>
				{rows.length > 0 && !isRatio && dimension !== 'category' && (
					<tfoot>
						<tr>
							<td className="chain-sticky" style={{ left: 0 }}>{t('chain_plans_sum_dimension')}</td>
							{poses.map(pos => {
								const totalPlan = parseValue(draft[salesKey(pos.id, 'total', 0, metric)])
								const sumPlan = rows.reduce((total, item) => total + cell(pos.id, item.id), 0)
								const coverage = totalPlan ? Math.round(sumPlan / totalPlan * 100) : null
								return (
									<td key={pos.id} className="chain-num" title={totalPlan ? `${t('chain_plans_branch_total')}: ${money(totalPlan)}` : undefined}>
										{money(sumPlan)}
										{coverage !== null && (
											<div className={`chain-plan-coverage ${coverage === 100 ? 'ok' : 'warn'}`}>
												{coverage}% {t('chain_plans_of_branch_plan')}
											</div>
										)}
									</td>
								)
							})}
							<td className="chain-num chain-total-col">
								{money(poses.reduce((total, pos) => total + rows.reduce((sum, item) => sum + cell(pos.id, item.id), 0), 0))}
							</td>
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	)
}

function ExpenseTable({ poses, rows, draft, base, refMap, onChange }) {
	const { t } = useTranslation()
	const cell = (posId, id) => parseValue(draft[expenseKey(posId, id)]) || 0

	return (
		<div className="chain-scroll">
			<table className="chain-table chain-plan-table">
				<thead>
					<tr>
						<th className="chain-name chain-sticky" style={{ left: 0 }}>{t('chain_purpose')}</th>
						{poses.map((pos, index) => (
							<th key={pos.id} className="text-end">
								<span className="chain-dot" style={{ background: chainColor(index) }}></span>
								{pos.name}
							</th>
						))}
						<th className="text-end chain-total-col">{t('total')}</th>
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 && <ChainEmpty colSpan={poses.length + 2} text={t('chain_plans_no_values')} />}
					{rows.map((item, rowIndex) => (
						<tr key={item.id}>
							<td className="chain-name chain-sticky" style={{ left: 0 }}>{item.name}</td>
							{poses.map((pos, colIndex) => (
								<PlanCell key={pos.id} cellKey={expenseKey(pos.id, item.id)} draft={draft} base={base}
									refMap={refMap} onChange={onChange} row={rowIndex} col={colIndex} />
							))}
							<td className="chain-num chain-total-col">{money(poses.reduce((total, pos) => total + cell(pos.id, item.id), 0))}</td>
						</tr>
					))}
				</tbody>
				{rows.length > 0 && (
					<tfoot>
						<tr>
							<td className="chain-sticky" style={{ left: 0 }}>{t('total')}</td>
							{poses.map(pos => (
								<td key={pos.id} className="chain-num">{money(rows.reduce((total, item) => total + cell(pos.id, item.id), 0))}</td>
							))}
							<td className="chain-num chain-total-col">
								{money(poses.reduce((total, pos) => total + rows.reduce((sum, item) => sum + cell(pos.id, item.id), 0), 0))}
							</td>
						</tr>
					</tfoot>
				)}
			</table>
		</div>
	)
}

export default Plans
